import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";

interface AddAccountModalProps {
  show: boolean;
  onHide: () => void;
  selectedBroker: string;
  accountId?: string;
  setAccountId: (id: string) => void;
  apiKey?: string;
  setApiKey: (key: string) => void;
  apiSecret?: string;
  setApiSecret: (secret: string) => void;
  serverName?: string;
  setServerName: (name: string) => void;
  accountNumber?: string;
  setAccountNumber: (num: string) => void;
  accountType: string;
  setAccountType: (type: string) => void;
  handleAddAccount: (brokerName: string) => void;
  isLoading?: boolean;
}

const AddAccountModal: React.FC<AddAccountModalProps> = ({
  show,
  onHide,
  selectedBroker,
  accountId,
  setAccountId,
  apiKey,
  setApiKey,
  apiSecret,
  setApiSecret,
  serverName,
  setServerName,
  accountNumber,
  setAccountNumber,
  accountType,
  setAccountType,
  handleAddAccount,
  isLoading = false,
}) => {
  const [localBrokerName, setLocalBrokerName] = useState<string>("");

  useEffect(() => {
    if (show && selectedBroker) {
      console.log("AddAccountModal opened with selectedBroker:", selectedBroker);
      setLocalBrokerName(selectedBroker);

      // Default port for IBKR if accountNumber is empty
      const broker = selectedBroker.toLowerCase();
      if ((broker.includes("interactive") || broker.includes("ibkr")) && !accountNumber) {
        setAccountNumber("7498");
      }
    }
  }, [show, selectedBroker]);

  const broker = localBrokerName.toLowerCase();

  // Visibility rules
  const isIBKR = broker.includes("interactive") || broker.includes("ibkr");
  const showAccountId = ["tradelocker", "oanda"].includes(broker) && !isIBKR;
  const showApiKey = !["interactive brokers", "ibkr", "tradelocker"].includes(broker);
  const showApiSecret = !["interactive brokers", "ibkr", "oanda", "tradelocker"].includes(broker);
  const showServerName = ["tradelocker", "binance", "binance.us"].includes(broker);
  const showAccountNumber = ["tradelocker", "charles schwab", "schwab"].includes(broker);
  const showPort = isIBKR;
  const showAccountType = !isIBKR;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Add Account to {localBrokerName || selectedBroker}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {showAccountId && (
          <Form.Group className="mb-3">
            <Form.Label>Account ID</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Account ID"
              value={accountId || ""}
              onChange={(e) => setAccountId(e.target.value)}
            />
          </Form.Group>
        )}

        {showAccountNumber && (
          <Form.Group className="mb-3">
            <Form.Label>Account Number</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Account Number"
              value={accountNumber || ""}
              onChange={(e) => setAccountNumber(e.target.value)}
            />
          </Form.Group>
        )}

        {showPort && (
          <Form.Group className="mb-3">
            <Form.Label>Port</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Port (Default: 7498)"
              value={accountNumber || ""}
              onChange={(e) => setAccountNumber(e.target.value)}
            />
          </Form.Group>
        )}

        {showServerName && (
          <Form.Group className="mb-3">
            <Form.Label>Server Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Server Name"
              value={serverName || ""}
              onChange={(e) => setServerName(e.target.value)}
            />
          </Form.Group>
        )}

        {(broker.includes("interactive") || broker.includes("ibkr")) && (
          <div className="alert alert-info">
            Ensure TWS or IB Gateway is running and configured to listen on the
            port defined in your connection settings (Default: 7497 for Paper,
            7496 for Live). Enable "ActiveX and Socket Clients" in TWS API
            Settings.
          </div>
        )}

        {showAccountType && (
          <Form.Group className="mb-3">
            <Form.Label>
              Account Type <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={accountType}
              onChange={(e) => setAccountType(e.target.value)}
            >
              <option value="">Select Account Type</option>
              <option value="paper">Paper Trading</option>
              <option value="live">Live Trading</option>
            </Form.Select>
          </Form.Group>
        )}

        {showApiKey && (
          <Form.Group className="mb-3">
            <Form.Label>{broker === "charles schwab" ? "Client ID / App Key" : "API Key / Token"}</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter your API Key or Token"
              value={apiKey || ""}
              onChange={(e) => setApiKey(e.target.value)}
            />
          </Form.Group>
        )}

        {showApiSecret && (
          <Form.Group className="mb-3">
            <Form.Label>{broker === "charles schwab" ? "App Secret" : "API Secret"}</Form.Label>
            <Form.Control
              type="password"
              placeholder="Enter your API Secret"
              value={apiSecret || ""}
              onChange={(e) => setApiSecret(e.target.value)}
            />
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            console.log("Submitting with localBrokerName:", localBrokerName);
            handleAddAccount(localBrokerName);
          }}
          disabled={((!accountType && !isIBKR) || isLoading)}
        >
          {isLoading ? "Adding..." : "Add Account"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddAccountModal;
